import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CardMedia, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNetworkDetail } from '../hooks';
import { Company } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

const NetworkDetailPage: React.FC = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('redes', 'editar');
  const {
    network,
    companies,
    eligibleCompanies,
    eligibleNextCursor,
    nextCursor,
    loading,
    error,
    loadMore,
    searchEligible,
    loadMoreEligible,
    setCompanyNetwork,
  } = useNetworkDetail(id);
  const [eligibleId, setEligibleId] = useState('');
  const [eligibleSearch, setEligibleSearch] = useState('');
  const [feedback, setFeedback] = useState<string>();

  useEffect(() => {
    setEligibleId('');
  }, [network]);

  const changeNetwork = async (company: Company, target: string | null) => {
    try {
      await setCompanyNetwork(company, target);
    } catch (caught) {
      const conflict = caught instanceof Error && caught.message.includes('aborted');
      setFeedback(conflict ? 'O vínculo mudou em outra sessão. A lista foi recarregada.' : 'Não foi possível atualizar o vínculo.');
    }
  };

  const selectEligibleCompany = (companyId: string) => {
    setEligibleId(companyId);
  };

  const linkSelectedCompany = () => {
    const company = eligibleCompanies.find((item) => item.id === eligibleId);
    if (company && network) void changeNetwork(company, network.id);
  };

  if (loading && !network) return <CircularProgress />;
  if (!network) return <Alert severity="error">{error || 'Rede não encontrada.'}</Alert>;

  return <Box>
    <Typography variant="h4">{network.name}</Typography>
    {network.description && <Typography sx={{ mb: 2 }}>{network.description}</Typography>}
    {network.imageUrl && <CardMedia component="img" image={network.imageUrl} alt={`Imagem da Rede ${network.name}`} sx={{ maxWidth: 320, maxHeight: 200, objectFit: 'contain', mb: 2 }} />}
    {(error || feedback) && <Alert severity="error" sx={{ my: 2 }}>{feedback || error}</Alert>}
    <Paper>
      <Table>
        <TableHead><TableRow><TableCell>Empresa vinculada</TableCell><TableCell>CNPJ</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {companies.length === 0 ? <TableRow><TableCell colSpan={3}>Nenhuma Empresa vinculada.</TableCell></TableRow>
            : companies.map((company) => <TableRow key={company.id}>
              <TableCell>{company.name}</TableCell><TableCell>{company.cnpj}</TableCell>
              <TableCell>
                <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Redes.'}>
                  <span>
                    <Button color="error" disabled={!canManage} onClick={() => void changeNetwork(company, null)}>Desvincular</Button>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>)}
        </TableBody>
      </Table>
    </Paper>
    {nextCursor && <Button onClick={() => void loadMore()} sx={{ mt: 2 }}>Carregar mais</Button>}
    <Typography variant="h6" sx={{ mt: 4 }}>Vincular Empresa</Typography>
    <TextField size="small" label="Buscar Empresa" value={eligibleSearch} onChange={(event) => { const value = event.target.value; setEligibleSearch(value); void searchEligible(value); }} sx={{ mr: 1 }} />
    <Select value={eligibleId} displayEmpty onChange={(event) => selectEligibleCompany(event.target.value)}>
      <MenuItem value="">Selecione uma Empresa</MenuItem>
      {eligibleCompanies.map((company) => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
    </Select>
    {eligibleNextCursor && <Button onClick={() => void loadMoreEligible(eligibleSearch)}>Mais resultados</Button>}
    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Redes.'}>
      <span>
        <Button sx={{ ml: 1 }} variant="contained" disabled={!canManage || !eligibleId} onClick={linkSelectedCompany}>Vincular</Button>
      </span>
    </Tooltip>
  </Box>;
};

export default NetworkDetailPage;
